import React, { createContext, useContext, useEffect, useState } from "react";

// Define the WASM context type
interface WasmContextType {
  wasm: any;
  isWasmLoaded: boolean;
}

const WasmContext = createContext<WasmContextType | undefined>(undefined);

export const useWasm = () => {
  const context = useContext(WasmContext);
  if (!context) {
    throw new Error("useWasm must be used within a WasmProvider");
  }
  return context;
};

export const WasmProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [wasm, setWasm] = useState<any>(null);
  const [isWasmLoaded, setIsWasmLoaded] = useState(false);

  useEffect(() => {
    const loadWasm = async () => {
      try {
        // Load wasm_exec.js
        const go = new (window as any).Go();
        const wasmPath = "/wasm/main.wasm";

        // Fetch and instantiate WASM
        const response = await fetch(wasmPath);
        if (!response.ok) {
          throw new Error(`Failed to fetch WASM: ${response.statusText}`);
        }
        const buffer = await response.arrayBuffer();
        const result = await WebAssembly.instantiate(buffer, go.importObject);

        // Run the WASM module
        go.run(result.instance);

        // Set global WASM functions
        setWasm(window as any);
        setIsWasmLoaded(true);
      } catch (error) {
        console.error("Error loading WASM:", error);
      }
    };

    // Load wasm_exec.js dynamically
    const script = document.createElement("script");
    script.src = "/wasm/wasm_exec.js";
    script.async = true;
    document.body.appendChild(script);
    script.onload = () => loadWasm();

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  return (
    <WasmContext.Provider value={{ wasm, isWasmLoaded }}>
      {children}
    </WasmContext.Provider>
  );
};
